import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, assetUrl } from "@/lib/api";
import { applySeo, localizedSlug } from "@/lib/utils";
import { Reveal } from "@/components/site/Reveal";

export default function PortfolioDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const P = t.portfolio;
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [all, setAll] = useState([]);

  useEffect(() => {
    setProject(null);
    setNotFound(false);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    api.get("/projects").then((r) => setAll(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!project) return;
    const desired = (lang === "ro" ? project.slug_ro : project.slug_en) || project.slug;
    if (desired && desired !== slug) navigate(`/portfolio/${desired}`, { replace: true });
  }, [lang, project, slug, navigate]);

  useEffect(() => {
    if (!project) return;
    applySeo({
      title: project[`seo_title_${lang}`] || `${g("title")} — Oana Mudura`,
      description: project[`meta_description_${lang}`] || g("intro"),
      path: window.location.pathname,
      enPath: `/portfolio/${project.slug_en || project.slug}`,
      roPath: `/portfolio/${project.slug_ro || project.slug}`,
    });
  }, [project, lang]);

  const g = (k) => (project ? project[`${k}_${lang}`] || project[`${k}_ro`] || "" : "");
  const list = (k) => (project ? project[`${k}_${lang}`] || project[`${k}_ro`] || [] : []);
  const L = {
    discipline: lang === "ro" ? "Disciplină" : "Discipline",
    role: lang === "ro" ? "Rol" : "Role",
    year: lang === "ro" ? "An" : "Year",
    strategy: lang === "ro" ? "Strategia" : "The strategy",
    judgment: lang === "ro" ? "Abordarea strategică" : "The judgment",
    results: P.achievementsLabel || (lang === "ro" ? "Rezultate" : "Results"),
    next: lang === "ro" ? "Următorul proiect" : "Next project",
  };

  if (notFound)
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center gap-6">
        <p className="eyebrow text-ink/50">404</p>
        <Link to="/portfolio" className="eyebrow underline">{P.backCta}</Link>
      </div>
    );

  if (!project) return <div className="min-h-screen bg-paper flex items-center justify-center eyebrow text-ink/40">…</div>;

  const paras = g("narrative").split(/\n\n+/).filter(Boolean);
  const achievements = list("achievements");
  const judgment = g("intro") || paras[0] || "";
  const vertical = (achievements.slice(0, 3).join("  ·  ") || g("category")).toUpperCase();

  const idx = all.findIndex((p) => p.id === project.id);
  const total = all.length;
  const num = String((idx >= 0 ? idx + 1 : project.order || 1)).padStart(2, "0");
  const next = idx >= 0 && total > 1 ? all[(idx + 1) % total] : null;

  const meta = [
    g("category") && { label: L.discipline, value: g("category") },
    g("role") && { label: L.role, value: g("role") },
    project.year && { label: L.year, value: project.year },
  ].filter(Boolean);

  const sideImage = assetUrl(project.gallery?.[0]) || assetUrl(project.cover_image);

  return (
    <div className="bg-paper text-ink min-h-screen">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20 pt-28 md:pt-36">
        <Link to="/portfolio" data-testid="project-back" data-cursor="link" className="eyebrow inline-flex items-center gap-2 text-ink/50 hover:text-ink transition-colors mb-10 md:mb-14">
          <ArrowLeft size={14} /> {P.backCta}
        </Link>
        <div className="grid grid-cols-12 gap-x-4 gap-y-2 items-start">
          <div className="col-span-3 md:col-span-2">
            <div className="proj-number text-[clamp(3.5rem,15vw,12rem)] text-ink">{num}</div>
          </div>
          <div className="col-span-9 md:col-span-10">
            <h1 data-testid="project-title" className="proj-title text-[clamp(2.4rem,12vw,9rem)]">{g("title")}</h1>
          </div>
        </div>

        {/* Metadata row */}
        {meta.length > 0 && (
          <div className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-ink/25">
            {meta.map((m, i) => (
              <div key={i} className="py-4 md:py-5 md:pr-5 border-b border-ink/12 md:border-b-0 md:border-r md:border-ink/12 last:border-r-0">
                <div className="eyebrow text-ink/45">{m.label}</div>
                <div className="font-display text-lg md:text-xl mt-1.5 uppercase tracking-tight">{m.value}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Cover with blue accent + vertical label */}
      {project.cover_image && (
        <section className="px-6 md:px-12 lg:px-20 mt-10 md:mt-16">
          <div className="relative">
            {vertical && (
              <div className="hidden lg:flex absolute left-0 top-0 h-full items-center -translate-x-[calc(100%+1.5rem)]">
                <span className="vert-label eyebrow text-ink/45 whitespace-nowrap">{vertical}</span>
              </div>
            )}
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-y-4 right-0 w-1/3 hidden md:block bg-brand" />
                <div className="relative overflow-hidden aspect-[16/10] md:aspect-[21/9] group">
                  <img src={assetUrl(project.cover_image)} alt={g("title")} className="duo w-full h-full object-cover" />
                </div>
              </div>
            </Reveal>
            <div className="mt-4 eyebrow text-ink/40 tnum">
              {num}{total > 1 ? ` / ${String(total).padStart(2, "0")}` : ""}
            </div>
          </div>
        </section>
      )}

      {/* Strategy + side image */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 md:gap-10 lg:gap-16 items-start">
          <div className="col-span-12 lg:col-span-5">
            <h2 className="proj-title text-[clamp(2.2rem,8vw,4.5rem)] mb-8">{L.strategy}</h2>
            <div className="space-y-6">
              {paras.length > 0 ? (
                paras.map((p, i) => (
                  <p key={i} className={i === 0 ? "text-xl md:text-2xl leading-[1.4] text-ink/90 font-light" : "text-lg leading-[1.7] text-ink/70"}>{p}</p>
                ))
              ) : (
                <p className="text-xl leading-[1.5] text-ink/80 font-light">{g("intro")}</p>
              )}
            </div>

            {judgment && (
              <Reveal>
                <div className="mt-12 p-7 md:p-9 bg-brand" data-testid="judgment-block">
                  <div className="eyebrow text-paper/65 mb-5">{L.judgment}</div>
                  <p className="serif text-2xl md:text-4xl leading-[1.12] text-paper">{judgment}</p>
                </div>
              </Reveal>
            )}
          </div>

          {sideImage && (
            <div className="col-span-12 lg:col-span-6 lg:col-start-7">
              <Reveal>
                <div className="relative">
                  <div className="absolute -bottom-4 left-6 right-6 h-24 hidden md:block bg-brand" />
                  <div className="relative overflow-hidden aspect-[4/5] group">
                    <img src={sideImage} alt={g("title")} className="duo w-full h-full object-cover" />
                  </div>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      {/* Results / stats band */}
      {achievements.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 pb-20 md:pb-28">
          <div className="eyebrow text-ink/45 mb-8">{L.results}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-ink/25">
            {achievements.map((a, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div className="py-8 md:py-10 md:px-7 first:md:pl-0 border-b border-ink/12 md:border-b-0 md:border-r md:border-ink/12 last:border-r-0">
                  <div className="proj-title text-[clamp(1.9rem,4.5vw,3.25rem)] leading-[0.95]">{a}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Gallery (remaining images) */}
      {project.gallery && project.gallery.length > 1 && (
        <section className="px-6 md:px-12 lg:px-20 pb-20 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {project.gallery.slice(1).map((img, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <div className="overflow-hidden aspect-[4/3] group">
                  <img src={assetUrl(img)} alt="" className="duo w-full h-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Next project */}
      <section className="px-6 md:px-12 lg:px-20 py-12 md:py-16 border-t border-ink/25">
        <Link
          to={next ? `/portfolio/${localizedSlug(next, lang)}` : "/portfolio"}
          data-testid="next-project"
          data-cursor="link"
          className="group flex items-center gap-5 md:gap-8"
        >
          <ArrowRight size={28} className="shrink-0 transition-transform duration-500 group-hover:translate-x-2" />
          <span className="proj-title text-[clamp(1.75rem,6vw,3.25rem)]">{L.next}</span>
          {next && (
            <span className="ml-auto hidden sm:block eyebrow text-ink/40 text-right max-w-[40%] truncate">
              {next[`title_${lang}`] || next.title_ro}
            </span>
          )}
        </Link>
      </section>
    </div>
  );
}
